/**
 * Mobile viewport detection for route variant selection (desktop vs mobile routeId).
 *
 * Used by `Routes.jsx` (layout + route set), sidebar chunk preload, and post-login
 * chunk warmup. `getRouteVariantSnapshot()` is the non-hook read for effects outside
 * React render (AuthStoreHydrator, Sidebar preload handlers).
 *
 * @module hooks/useMobileViewport
 * @see docs/route-chunk-preload.md — mobile routeId rules
 */
import { useEffect, useState } from "react";

const MOBILE_WIDTH_QUERY = "(max-width: 820px)";
/** Tracks tall phone presets (e.g. Pro Max ~956 CSS px portrait); ancillary to `isMobileWidth`. */
const MOBILE_HEIGHT_QUERY = "(max-height: 980px)";

/**
 * Phone-class viewport bounds (min/max of inner width/height so landscape still matches).
 * Chrome DevTools “iPhone Pro Max size” uses ~440×956 CSS px; older gens ~430×932.
 * Caps leave slack for browser chrome / safe-area / newer presets.
 */
const PHONE_CLASS_SHORT_SIDE_MAX = 480;
const PHONE_CLASS_LONG_SIDE_MAX = 980;
const MOBILE_PHONE_UA_REGEX =
  /Mobi|iPhone|iPod|Android.*Mobile|BlackBerry|IEMobile|Opera Mini|webOS/i;

function getIsMobilePhoneDevice() {
  if (typeof navigator === "undefined") return false;
  try {
    const uaData = navigator.userAgentData;
    if (uaData && typeof uaData.mobile === "boolean") {
      return uaData.mobile;
    }
  } catch {
    // Client Hints unavailable (e.g. privacy mode); fall through to UA heuristics.
  }

  const ua = navigator.userAgent || "";
  if (/iPad/i.test(ua)) return false;
  // iPadOS desktop-mode Safari reports Macintosh in UA; real Macs typically have maxTouchPoints 0.
  if (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1) return false;
  if (/Android/i.test(ua) && !/Mobile/i.test(ua)) return false;
  return MOBILE_PHONE_UA_REGEX.test(ua);
}

function getPhoneClassViewport() {
  if (typeof window === "undefined") return false;
  const w = window.innerWidth;
  const h = window.innerHeight;
  const shortSide = Math.min(w, h);
  const longSide = Math.max(w, h);
  return (
    shortSide <= PHONE_CLASS_SHORT_SIDE_MAX && longSide <= PHONE_CLASS_LONG_SIDE_MAX
  );
}

function getMediaMatch(query) {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia(query).matches;
}

/**
 * Shell-level “mobile”: real phone in a phone-sized viewport, OR a viewport that hits both
 * mobile width and height breakpoints (responsive devtools / small desktop windows).
 */
function resolveIsMobileViewport(isMobileWidthMq, isMobileHeightMq) {
  const phoneShell = getIsMobilePhoneDevice() && getPhoneClassViewport();
  const compactViewport = isMobileWidthMq && isMobileHeightMq;
  return phoneShell || compactViewport;
}

function getViewportFlags() {
  if (typeof window === "undefined" || !window.matchMedia) {
    return {
      isMobileWidth: false,
      isMobileHeight: false,
      isMobileViewport: false,
    };
  }

  const isMobileWidth = getMediaMatch(MOBILE_WIDTH_QUERY);
  const isMobileHeight = getMediaMatch(MOBILE_HEIGHT_QUERY);
  const isMobileViewport = resolveIsMobileViewport(
    isMobileWidth,
    isMobileHeight,
  );
  return {
    isMobileWidth,
    isMobileHeight,
    isMobileViewport,
  };
}

export function getRouteVariantSnapshot() {
  // Non-hook read for preload paths (AuthStoreHydrator, Sidebar) outside React render.
  return getViewportFlags();
}

export function useMobileViewport() {
  const [viewportFlags, setViewportFlags] = useState(getViewportFlags);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const widthMedia = window.matchMedia(MOBILE_WIDTH_QUERY);
    const heightMedia = window.matchMedia(MOBILE_HEIGHT_QUERY);

    const refreshFlags = () => {
      const isMobileWidth = widthMedia.matches;
      const isMobileHeight = heightMedia.matches;
      setViewportFlags({
        isMobileWidth,
        isMobileHeight,
        isMobileViewport: resolveIsMobileViewport(
          isMobileWidth,
          isMobileHeight,
        ),
      });
    };

    const addMediaListener = (mediaQueryList, handler) => {
      if (typeof mediaQueryList.addEventListener === "function") {
        mediaQueryList.addEventListener("change", handler);
      } else if (typeof mediaQueryList.addListener === "function") {
        mediaQueryList.addListener(handler);
      }
    };

    const removeMediaListener = (mediaQueryList, handler) => {
      if (typeof mediaQueryList.removeEventListener === "function") {
        mediaQueryList.removeEventListener("change", handler);
      } else if (typeof mediaQueryList.removeListener === "function") {
        mediaQueryList.removeListener(handler);
      }
    };

    refreshFlags();
    addMediaListener(widthMedia, refreshFlags);
    addMediaListener(heightMedia, refreshFlags);
    window.addEventListener("resize", refreshFlags);
    window.addEventListener("orientationchange", refreshFlags);

    return () => {
      removeMediaListener(widthMedia, refreshFlags);
      removeMediaListener(heightMedia, refreshFlags);
      window.removeEventListener("resize", refreshFlags);
      window.removeEventListener("orientationchange", refreshFlags);
    };
  }, []);

  return viewportFlags;
}

export default useMobileViewport;
